import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Image } from 'expo-image';
import { Camera, RefreshCw } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';

interface ImagePickerSectionProps {
  imageUri: string | null;
  onPickImage: () => void;
}

export function ImagePickerSection({ imageUri, onPickImage }: ImagePickerSectionProps) {
  const { colors } = useTheme();

  return (
    <TouchableOpacity
      // No fill: `surface` over `background` is a 1-step tint that swallows
      // the hairline and turns the empty state into a grey slab. The border
      // is the frame.
      style={[styles.imagePicker, { borderColor: colors.border }]}
      onPress={onPickImage}
      accessibilityRole="button"
      accessibilityLabel={imageUri ? 'Change photo' : 'Add photo'}
      activeOpacity={0.8}>
      {imageUri ? (
        <Image
          source={imageUri}
          style={styles.selectedImage}
          contentFit="cover"
          cachePolicy="memory-disk"
        />
      ) : (
        <View style={styles.imagePickerContent}>
          <Camera size={28} color={colors.text} />
          <Text style={[styles.imagePickerText, { color: colors.text }]}>Add Photo</Text>
          <Text style={[styles.imagePickerHint, { color: colors.textSecondary }]}>
            Document your observation with a photo
          </Text>
        </View>
      )}
      {imageUri && (
        <View
          style={[styles.changeImageButton, { backgroundColor: colors.overlay }]}
          pointerEvents="none">
          <RefreshCw size={16} color={colors.white} />
          <Text style={[styles.changeImageText, { color: colors.white }]}>Change</Text>
        </View>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  // Plaud grammar: a hairline frame, not a dashed box with a tinted circle.
  // 4:3 matches the frame a posted photo renders in.
  imagePicker: {
    aspectRatio: 4 / 3,
    // The reading column is 736px wide on the desktop shell; unclamped, 4:3
    // makes the empty drop zone taller than the fold.
    maxHeight: 320,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    overflow: 'hidden',
    marginBottom: SPACING.lg,
  },
  imagePickerContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.xl,
    gap: SPACING.xs,
  },
  imagePickerText: {
    ...TYPOGRAPHY.h4,
  },
  imagePickerHint: {
    ...TYPOGRAPHY.bodySmall,
    textAlign: 'center',
  },
  selectedImage: {
    width: '100%',
    height: '100%',
  },
  changeImageButton: {
    position: 'absolute',
    bottom: SPACING.md,
    right: SPACING.md,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.smd,
    borderRadius: BORDER_RADIUS.full,
  },
  changeImageText: {
    ...TYPOGRAPHY.button,
    marginLeft: SPACING.xs,
  },
});
